import { useEffect, useCallback } from 'react';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | 'disclaimer' | null;
}

const privacyContent = (
  <div className="space-y-5 md:space-y-6">
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Information We Collect</h3>
      <div className="font-body text-xs md:text-sm text-white/70 leading-relaxed space-y-3">
        <p>When you submit an enquiry through our WhatsApp integration, we collect your name and phone number solely to respond to your query about Confident Auriga plots and site visits.</p>
        <p>We may also collect basic analytics data such as page views and interaction patterns via standard cookies to improve your browsing experience.</p>
        <p>By initiating a WhatsApp conversation or submitting your contact details, you consent to being contacted by our team via WhatsApp, SMS, or phone call regarding project updates, offers, and site visit scheduling.</p>
      </div>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">How We Use Your Information</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        Your contact information is used exclusively to communicate with you about Confident Auriga products, services, offers, and project updates. We do not sell or share your personal data with third-party marketers. WhatsApp enquiry data is transmitted directly to our sales team for immediate follow-up.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Cookies & Third-Party Services</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        This website uses essential cookies to ensure proper functionality and analytical cookies to understand visitor behavior. Third-party services such as Google Maps and WhatsApp may set their own cookies in accordance with their respective privacy policies. You can manage cookie preferences through your browser settings.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Data Privacy & Consent</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        You have the right to request access to, correction of, or deletion of your personal data at any time. To exercise these rights, contact us directly via WhatsApp or phone. You may withdraw your consent for communication at any time by messaging "STOP" to our WhatsApp number or calling our helpline.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div className="bg-gold/10 border border-gold/20 rounded-xl p-4 md:p-5">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-shield-check-line text-gold text-base" />
        </div>
        <div>
          <h4 className="font-display text-sm md:text-base text-white font-semibold mb-1">Communication Consent</h4>
          <p className="font-body text-xs md:text-sm text-white/60 leading-relaxed">
            I agree to be contacted by Confident Group via WhatsApp, SMS, phone call, or other communication channels for product information, offers, and marketing updates. I understand I can withdraw my consent anytime. This consent may override DNC/NDNC registration where legally permitted.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const termsContent = (
  <div className="space-y-5 md:space-y-6">
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Plot Enquiry & Availability</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        All plot enquiries submitted through this website are subject to availability at the time of response. Confident Auriga reserves the right to allocate plots on a first-come, first-served basis. Pricing and availability are subject to change without prior notice. A confirmed booking is only valid upon receipt of the prescribed booking amount and execution of the official sale agreement.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Site Visits</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        Site visits are scheduled based on availability and prior booking confirmation. Visitors must carry valid government-issued identification. Confident Auriga reserves the right to cancel or reschedule site visits due to weather conditions, operational constraints, or safety concerns. Children and minors must be accompanied by adults at all times during site visits.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Information Accuracy</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        While every effort is made to ensure the accuracy of project information, specifications, amenities, and layouts displayed on this website, Confident Auriga does not guarantee that all details are error-free. Project specifications, amenities, and layouts may be modified during the development phase to comply with regulatory requirements or to improve the overall project quality.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Marketing Communication</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        By submitting an enquiry or initiating contact through this website, you consent to receive marketing and promotional communications from Confident Auriga and its authorized representatives. You may opt out at any time by contacting us directly. All promotional offers are time-bound and subject to terms and conditions specified at the time of the offer.
      </p>
    </div>
  </div>
);

const disclaimerContent = (
  <div className="space-y-5 md:space-y-6">
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Representation Disclaimer</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        All images, renders, photographs, and visual representations on this website are for illustration and representation purposes only. The actual development may differ from the visuals shown. Architectural designs, landscaping, amenities, and finishes are subject to change based on final approvals, material availability, and design optimization during construction.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Project Details Subject to Change</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        Project details including but not limited to plot sizes, pricing, amenities, layout plans, and possession timelines are indicative and subject to change without prior notice. Final details will be as per the approved plans and agreements. Prospective buyers are advised to verify all information independently before making any investment decision.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div>
      <h3 className="font-display text-base md:text-lg text-white font-semibold mb-2 md:mb-3">Availability & Verification</h3>
      <p className="font-body text-xs md:text-sm text-white/70 leading-relaxed">
        All plot availability is subject to confirmation at the time of enquiry. Confident Auriga does not guarantee the continued availability of any specific plot number or size. Prospective buyers must verify all project details, legal clearances, and approvals directly with our sales team or authorized representatives before completing any purchase.
      </p>
    </div>
    <div className="w-full h-px bg-white/10" />
    <div className="bg-gold/10 border border-gold/20 rounded-xl p-4 md:p-5">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-alert-line text-gold text-base" />
        </div>
        <div>
          <h4 className="font-display text-sm md:text-base text-white font-semibold mb-1">Investment Advisory</h4>
          <p className="font-body text-xs md:text-sm text-white/60 leading-relaxed">
            Real estate investments carry inherent risks. Past performance and projected returns are not guarantees of future results. We strongly recommend conducting independent due diligence, consulting legal and financial advisors, and verifying all documentation before investing in any plotted development.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const titles = {
  privacy: 'Privacy Policy',
  terms: 'Terms & Conditions',
  disclaimer: 'Disclaimer',
};

export default function LegalModal({ isOpen, onClose, type }: LegalModalProps) {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen || !type) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 md:p-8 overflow-y-auto animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#0F2E24]/85 backdrop-blur-2xl transition-opacity duration-500"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-3xl bg-white/[0.06] backdrop-blur-3xl border border-white/10 rounded-2xl md:rounded-3xl shadow-2xl shadow-black/30 overflow-hidden my-4 md:my-8 animate-scale-in">
        {/* Top accent glow */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-10 py-4 md:py-5 bg-[#123524]/80 backdrop-blur-xl border-b border-white/10">
          <h2
            id="legal-modal-title"
            className="font-display text-lg md:text-2xl text-white font-bold"
          >
            {titles[type]}
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold/40 hover:bg-white/5 transition-all duration-300"
            aria-label="Close modal"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 py-6 md:py-8 max-h-[70vh] overflow-y-auto">
          {type === 'privacy' && privacyContent}
          {type === 'terms' && termsContent}
          {type === 'disclaimer' && disclaimerContent}
        </div>

        {/* Footer */}
        <div className="px-6 md:px-10 py-4 md:py-5 bg-white/[0.03] border-t border-white/10 flex items-center justify-between">
          <p className="font-body text-[10px] md:text-xs text-white/30 tracking-wider">
            Last updated: May 2026
          </p>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-5 py-2 bg-gold/90 hover:bg-gold text-[#0F2E24] font-body text-xs font-semibold rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
          >
            <i className="ri-check-line" />
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
}